import React, { useCallback, useEffect, useRef, useState } from "react";
import { Box, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, MenuItem, TextField, Typography } from "@material-ui/core";
import api from "../../services/api";
let nextSelectorId = 0;

export default function CodexAccounts({ onChange }) {
  const [selectorId] = useState(() => `codex-account-${++nextSelectorId}`);
  const [accounts, setAccounts] = useState([]), [busy, setBusy] = useState(false), [error, setError] = useState("");
  const [login, setLogin] = useState(null), [open, setOpen] = useState(false), [removing, setRemoving] = useState(null);
  const callback = useRef(onChange);
  callback.current = onChange;
  const load = useCallback(async () => {
    const [list, status] = await Promise.all([api.get("/fabio-ai/codex/accounts"), api.get("/fabio-ai/codex/status")]);
    setAccounts(list.data.accounts || []);
    callback.current?.(status.data);
  }, []);
  useEffect(() => { load().catch(() => setError("No fue posible consultar las cuentas de Codex.")); }, [load]);
  useEffect(() => {
    if (!login?.running) return undefined;
    let cancelled = false;
    const timer = setInterval(async () => {
      try {
        const { data } = await api.get("/fabio-ai/codex/login");
        if (cancelled) return;
        setLogin(data);
        if (data.connected) { setOpen(false); setError(""); await load(); }
      } catch { if (!cancelled) setError("No fue posible consultar la autorización. Puede reintentar."); }
    }, 3000);
    return () => { cancelled = true; clearInterval(timer); };
  }, [login?.running, load]);
  const perform = async action => {
    setBusy(true); setError("");
    try { await action(); } catch (e) { setError(e.response?.data?.error || "No fue posible completar la operación."); }
    finally { setBusy(false); }
  };
  const start = () => perform(async () => {
    const { data } = await api.post("/fabio-ai/codex/login", {});
    setLogin(data); setOpen(true);
  });
  const output = String(login?.output || "").replace(/\u001b\[[0-9;]*m/g, "");
  const code = output.match(/\b[A-Z0-9]{4}-[A-Z0-9]{4,5}\b/)?.[0];
  const active = accounts.find(a => a.active);
  return <Box mt={1}>
    {error && <Typography role="alert" color="error">{error}</Typography>}
    <Box display="flex" alignItems="center" flexWrap="wrap" style={{ gap: 12 }}>
      <TextField id={selectorId} select variant="outlined" size="small" label="Cuenta activa de Codex" value={active?.id || ""}
        disabled={busy || Boolean(login?.running) || !accounts.length} style={{ minWidth: 300, maxWidth: "100%" }}
        onChange={e => { const id = e.target.value; perform(async () => { await api.post("/fabio-ai/codex/accounts/activate", { id }); await load(); }); }}>
        <MenuItem value="" disabled>Seleccione una cuenta</MenuItem>
        {accounts.map(a => <MenuItem key={a.id} value={a.id}>{a.name || a.email}{a.name ? ` · ${a.email}` : ""}{a.active ? " · Activa" : ""}</MenuItem>)}
      </TextField>
      <Button variant="outlined" color="primary" disabled={busy} onClick={() => login?.running ? setOpen(true) : start()}>
        {login?.running ? "Ver conexión pendiente" : "Agregar otra cuenta"}
      </Button>
    </Box>
    {!accounts.length && <Typography variant="body2">No hay cuentas guardadas. Agregue una cuenta para conectar Fabio.</Typography>}
    {accounts.map(a => <Box key={a.id} display="flex" alignItems="center" flexWrap="wrap" mt={1}>
      <Typography variant="body2">{a.name || a.email}{a.name ? ` · ${a.email}` : ""}{a.active ? " · Activa" : ""}</Typography>
      <Button color="secondary" disabled={busy || Boolean(login?.running)} onClick={() => setRemoving(a)}>Eliminar cuenta</Button>
    </Box>)}
    <Typography variant="caption">La cuenta que seleccione será utilizada por Fabio. Al conectar otra cuenta, esta quedará activa.</Typography>
    <Dialog open={open} onClose={() => setOpen(false)} maxWidth="sm" fullWidth>
      <DialogTitle>Conectar otra cuenta de Codex</DialogTitle>
      <DialogContent>
        <Typography>Abra el enlace de OpenAI, inicie sesión con la cuenta que desea agregar e ingrese el código.</Typography>
        <Box textAlign="center" my={2}>{code ? <Typography variant="h4">{code}</Typography> : login?.running ? <CircularProgress size={28} /> : <Typography>No se generó el código. Puede reintentar.</Typography>}</Box>
        {login?.running === false && !login?.connected && <Typography color="error">La autorización terminó sin conectar la cuenta. Puede reintentar.</Typography>}
        <Button href="https://auth.openai.com/codex/device" target="_blank" rel="noopener noreferrer" disabled={!code || login?.running === false} variant="contained" color="primary">Abrir inicio de sesión de OpenAI</Button>
        <Typography variant="body2">Si aparece la cuenta anterior en OpenAI, cambie a la cuenta que desea agregar antes de autorizar.</Typography>
      </DialogContent>
      <DialogActions>{login?.running === false && <Button disabled={busy} onClick={start}>Reintentar</Button>}<Button onClick={() => setOpen(false)}>Cerrar</Button></DialogActions>
    </Dialog>
    <Dialog open={Boolean(removing)} onClose={() => !busy && setRemoving(null)}>
      <DialogTitle>Eliminar cuenta de SOLUWHATS</DialogTitle>
      <DialogContent><Typography>¿Confirma que desea eliminar la cuenta {removing?.email} de este servidor?</Typography>
        <Typography>{removing?.active ? "Esta cuenta está activa. Fabio quedará sin conexión con Codex hasta que seleccione o agregue otra cuenta." : "Podrá volver a agregarla iniciando sesión. Su cuenta de OpenAI no se elimina."}</Typography>
      </DialogContent>
      <DialogActions><Button disabled={busy} onClick={() => setRemoving(null)}>Cancelar</Button><Button color="secondary" disabled={busy} onClick={() => perform(async () => {
        await api.post("/fabio-ai/codex/accounts/delete", { id: removing.id, confirm: true }); setRemoving(null); await load();
      })}>Confirmar eliminación</Button></DialogActions>
    </Dialog>
  </Box>;
}
